import React from "react";
import { useSearchParams } from "react-router-dom";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import { requireLoginBannerPortal } from "../../../utils/loginBannerPortal";
import "./AdminSettings.css";

const formatFileSize = (size) => {
  const value = Number(size);
  if (!Number.isFinite(value) || value <= 0) return "0 KB";
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
};

const formatDateTime = (value) => {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (_err) {
    return String(value);
  }
};

const moveItemById = (items, draggedId, targetId) => {
  if (!draggedId || !targetId || draggedId === targetId) {
    return items;
  }

  const sourceIndex = items.findIndex((item) => item.id === draggedId);
  const targetIndex = items.findIndex((item) => item.id === targetId);

  if (sourceIndex === -1 || targetIndex === -1) {
    return items;
  }

  const nextItems = [...items];
  const [draggedItem] = nextItems.splice(sourceIndex, 1);
  nextItems.splice(targetIndex, 0, draggedItem);

  return nextItems.map((item, index) => ({
    ...item,
    position: index + 1,
  }));
};

const MACHINE_VIDEO_ASPECT_OPTIONS = [
  { value: "AUTO", label: "Automático" },
  { value: "16:9", label: "16:9 horizontal" },
  { value: "9:16", label: "9:16 vertical" },
];

const isMachineVideoLink = (video = {}) => video?.sourceType === "LINK";
const getMachineVideoAudienceSummary = (video = {}) => {
  const audiences = [];

  if (video?.licensedEnabled) audiences.push("Licenciado");
  if (video?.realgemsEnabled) audiences.push("Realgems");

  return audiences.length ? audiences.join(" + ") : "Nenhum portal";
};

const PRESENTATION_AUDIENCE_OPTIONS = [
  { value: "BOTH", label: "Licenciado e Realgems" },
  { value: "LICENSED", label: "Somente Licenciado" },
  { value: "REALGEMS", label: "Somente Realgems" },
];

const extractPresentationEmbedUrl = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const iframeMatch = text.match(/src\s*=\s*["']([^"']+)["']/i);
  const candidate = iframeMatch?.[1] || text;

  try {
    const url = new URL(candidate);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : "";
  } catch (_err) {
    return "";
  }
};

const createPresentationDraft = () => ({
  id: `presentation-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: "",
  embedCode: "",
  embedUrl: "",
  audience: "BOTH",
});

const getPresentationsFromSettings = (settings = {}) => {
  const sourceItems = Array.isArray(settings.items)
    ? settings.items
    : settings.embedCode || settings.embedUrl
      ? [
          {
            id: "presentation-1",
            title: "Apresentação",
            embedCode: settings.embedCode || settings.embedUrl,
            embedUrl: settings.embedUrl,
            audience: "BOTH",
          },
        ]
      : [];

  return sourceItems.map((item, index) => ({
    id: String(item?.id || `presentation-${index + 1}`),
    title: String(item?.title || `Apresentação ${index + 1}`),
    embedCode: String(item?.embedCode || item?.embedUrl || ""),
    embedUrl: String(item?.embedUrl || ""),
    audience: PRESENTATION_AUDIENCE_OPTIONS.some((option) => option.value === item?.audience)
      ? item.audience
      : "BOTH",
  }));
};

const BANNER_PORTAL_CONFIG = {
  "banner-mrv": { portal: "MRV", title: "Banner MRV", pageTitle: "MRV / Banner", settingsTitle: "Configurações do banner MRV", settingsDescription: "Gerencie o carrossel exibido no login /mrv.", countLabel: "banner MRV", emptyLabel: "Nenhum banner MRV enviado ainda. Adicione os primeiros itens acima." },
  "banner-inicial": {
    portal: "DEFAULT",
    title: "Banner inicial",
    pageTitle: "Configurações / Banner inicial",
    settingsTitle: "Configurações do banner inicial",
    settingsDescription:
      "Todas as opções abaixo controlam o carrossel exibido na tela inicial do sistema.",
    countLabel: "banner inicial",
    emptyLabel: "Nenhum banner enviado ainda. Adicione os primeiros itens acima.",
  },
  "banner-realgems": {
    portal: "REALGEMS",
    title: "Banner Realgems",
    pageTitle: "Configurações / Banner Realgems",
    settingsTitle: "Configurações do banner Realgems",
    settingsDescription:
      "Todas as opções abaixo controlam o carrossel exibido na tela de acesso do portal /realgems.",
    countLabel: "banner Realgems",
    emptyLabel: "Nenhum banner Realgems enviado ainda. Adicione os primeiros itens acima.",
  },
};

const DEFAULT_POINTS_FORM = {
  pointsPerReal: "1",
  campaignEnabled: false,
  campaignPointsPerReal: "",
  campaignStartAt: "",
  campaignEndAt: "",
};

const SETTINGS_TABS = new Set([
  "banner-inicial",
  "banner-realgems",
  "pontos",
  "manual-operacao",
  "portifolio-perfumaria",
  "apresentacoes",
]);

const toDateInputValue = (value) => {
  if (!value) return "";
  const text = String(value);
  const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

const getPointsFormFromSettings = (settings = {}) => ({
  pointsPerReal:
    settings.pointsPerReal === null || settings.pointsPerReal === undefined
      ? "1"
      : String(settings.pointsPerReal),
  campaignEnabled: Boolean(settings.campaignEnabled),
  campaignPointsPerReal:
    settings.campaignPointsPerReal === null || settings.campaignPointsPerReal === undefined
      ? ""
      : String(settings.campaignPointsPerReal),
  campaignStartAt: toDateInputValue(settings.campaignStartAt),
  campaignEndAt: toDateInputValue(settings.campaignEndAt),
});

const getSettingsPageTitle = (activeSubmenu) => {
  if (activeSubmenu === "pontos") return "Configurações / Pontos";
  if (activeSubmenu === "manual-operacao") {
    return "Configurações / Manual de operações";
  }
  if (activeSubmenu === "portifolio-perfumaria") {
    return "Configurações / Portfólio de perfumaria";
  }
  if (activeSubmenu === "apresentacoes") {
    return "Configurações / Apresentações";
  }
  if (activeSubmenu === "videos-maquinas") {
    return "Configurações / Vídeos de máquinas";
  }
  return "Configurações / Banner inicial";
};

const AdminSettings = ({ mrv = false }) => {
  const [menuClose, setMenuClose] = React.useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [banners, setBanners] = React.useState([]);
  const [bannerApiReady, setBannerApiReady] = React.useState(false);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [selectedMobileFiles, setSelectedMobileFiles] = React.useState([]);
  const [activeSubmenu, setActiveSubmenu] = React.useState(mrv ? "banner-mrv" : "banner-inicial");
  const [transitionDurationMs, setTransitionDurationMs] = React.useState("5000");
  const [pointsForm, setPointsForm] = React.useState(DEFAULT_POINTS_FORM);
  const [pointsUpdatedAt, setPointsUpdatedAt] = React.useState("");
  const [pointsLoaded, setPointsLoaded] = React.useState(false);
  const [manualEmbedCode, setManualEmbedCode] = React.useState("");
  const [manualEmbedUrl, setManualEmbedUrl] = React.useState("");
  const [manualUpdatedAt, setManualUpdatedAt] = React.useState("");
  const [manualLoaded, setManualLoaded] = React.useState(false);
  const [portfolioEmbedCode, setPortfolioEmbedCode] = React.useState("");
  const [portfolioEmbedUrl, setPortfolioEmbedUrl] = React.useState("");
  const [portfolioUpdatedAt, setPortfolioUpdatedAt] = React.useState("");
  const [portfolioLoaded, setPortfolioLoaded] = React.useState(false);
  const [presentations, setPresentations] = React.useState([]);
  const [presentationsUpdatedAt, setPresentationsUpdatedAt] = React.useState("");
  const [presentationsLoaded, setPresentationsLoaded] = React.useState(false);
  const [machineVideos, setMachineVideos] = React.useState([]);
  const [machineVideoTitle, setMachineVideoTitle] = React.useState("");
  const [machineVideoLink, setMachineVideoLink] = React.useState("");
  const [machineVideoAspectRatio, setMachineVideoAspectRatio] = React.useState("AUTO");
  const [machineVideoLicensedEnabled, setMachineVideoLicensedEnabled] = React.useState(true);
  const [machineVideoRealgemsEnabled, setMachineVideoRealgemsEnabled] = React.useState(true);
  const [selectedMachineVideoFile, setSelectedMachineVideoFile] = React.useState(null);
  const [editingMachineVideoId, setEditingMachineVideoId] = React.useState("");
  const [editingMachineVideoTitle, setEditingMachineVideoTitle] = React.useState("");
  const [editingMachineVideoLink, setEditingMachineVideoLink] = React.useState("");
  const [editingMachineVideoAspectRatio, setEditingMachineVideoAspectRatio] =
    React.useState("AUTO");
  const [editingMachineVideoLicensedEnabled, setEditingMachineVideoLicensedEnabled] =
    React.useState(true);
  const [editingMachineVideoRealgemsEnabled, setEditingMachineVideoRealgemsEnabled] =
    React.useState(true);
  const [machineVideosUpdatedAt, setMachineVideosUpdatedAt] = React.useState("");
  const [machineVideosLoaded, setMachineVideosLoaded] = React.useState(false);
  const [draggedBannerId, setDraggedBannerId] = React.useState("");
  const [draggedMachineVideoId, setDraggedMachineVideoId] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [loadingPoints, setLoadingPoints] = React.useState(true);
  const [loadingManual, setLoadingManual] = React.useState(true);
  const [loadingPortfolio, setLoadingPortfolio] = React.useState(true);
  const [loadingPresentations, setLoadingPresentations] = React.useState(true);
  const [loadingMachineVideos, setLoadingMachineVideos] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [savingPoints, setSavingPoints] = React.useState(false);
  const [savingManual, setSavingManual] = React.useState(false);
  const [savingPortfolio, setSavingPortfolio] = React.useState(false);
  const [savingPresentations, setSavingPresentations] = React.useState(false);
  const [uploadingMachineVideo, setUploadingMachineVideo] = React.useState(false);
  const [reorderingBanners, setReorderingBanners] = React.useState(false);
  const [savingMachineVideoId, setSavingMachineVideoId] = React.useState("");
  const [reorderingMachineVideos, setReorderingMachineVideos] = React.useState(false);
  const [deletingBannerId, setDeletingBannerId] = React.useState("");
  const [deletingMachineVideoId, setDeletingMachineVideoId] = React.useState("");
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const fileInputRef = React.useRef(null);
  const mobileFileInputRef = React.useRef(null);
  const machineVideoInputRef = React.useRef(null);
  const isBannerSubmenu = Boolean(BANNER_PORTAL_CONFIG[activeSubmenu]);
  const supportsMobileBanner = activeSubmenu === "banner-realgems" || activeSubmenu === "banner-mrv";
  const bannerConfig = BANNER_PORTAL_CONFIG[activeSubmenu] || BANNER_PORTAL_CONFIG["banner-inicial"];

  const toggleMenu = () => setMenuClose((prev) => !prev);

  React.useEffect(() => {
    if (mrv) { setActiveSubmenu("banner-mrv"); return; }
    const tab = searchParams.get("aba");
    if (SETTINGS_TABS.has(tab)) {
      setActiveSubmenu(tab);
    }
  }, [searchParams, mrv]);

  const handleSubmenuChange = (submenu) => {
    setActiveSubmenu(submenu);
    setSearchParams({ aba: submenu });
    setError("");
    setSuccess("");
  };

  const loadBanners = React.useCallback(async (portal = "DEFAULT") => {
    setBanners([]);
    setBannerApiReady(false);
    try {
      setLoading(true);
      setError("");
      const response = await Api.get("/public/login-banners", {
        params: { portal },
      });
      setBanners(requireLoginBannerPortal(response?.data, portal));
      setBannerApiReady(true);
      setTransitionDurationMs(String(response?.data?.transitionDurationMs || 5000));
    } catch (err) {
      console.error("Erro ao carregar banners:", err);
      setError(err?.message?.includes("API do servidor") ? err.message : "Não foi possível carregar os banners do carrossel.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (isBannerSubmenu) {
      loadBanners(bannerConfig.portal);
    }
  }, [bannerConfig.portal, isBannerSubmenu, loadBanners]);

  const loadPointsSettings = React.useCallback(async () => {
    try {
      setLoadingPoints(true);
      setError("");
      const response = await Api.get("/admin/points-settings");
      const settings = response?.data?.settings || {};
      setPointsForm(getPointsFormFromSettings(settings));
      setPointsUpdatedAt(settings.updatedAt || "");
      setPointsLoaded(true);
    } catch (err) {
      console.error("Erro ao carregar configurações de pontos:", err);
      setError("Não foi possível carregar as configurações de pontos.");
    } finally {
      setLoadingPoints(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeSubmenu === "pontos" && !pointsLoaded) {
      loadPointsSettings();
    }
  }, [activeSubmenu, loadPointsSettings, pointsLoaded]);

  const loadManualSettings = React.useCallback(async () => {
    try {
      setLoadingManual(true);
      setError("");
      const response = await Api.get("/admin/manual-operations");
      const settings = response?.data?.settings || {};
      setManualEmbedCode(String(settings.embedCode || ""));
      setManualEmbedUrl(String(settings.embedUrl || ""));
      setManualUpdatedAt(settings.updatedAt || "");
      setManualLoaded(true);
    } catch (err) {
      console.error("Erro ao carregar configurações do manual de operações:", err);
      setError("Não foi possível carregar as configurações do manual de operações.");
    } finally {
      setLoadingManual(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeSubmenu === "manual-operacao" && !manualLoaded) {
      loadManualSettings();
    }
  }, [activeSubmenu, loadManualSettings, manualLoaded]);

  const loadPortfolioSettings = React.useCallback(async () => {
    try {
      setLoadingPortfolio(true);
      setError("");
      const response = await Api.get("/admin/perfumery-portfolio");
      const settings = response?.data?.settings || {};
      setPortfolioEmbedCode(String(settings.embedCode || ""));
      setPortfolioEmbedUrl(String(settings.embedUrl || ""));
      setPortfolioUpdatedAt(settings.updatedAt || "");
      setPortfolioLoaded(true);
    } catch (err) {
      console.error("Erro ao carregar configurações do portfólio de perfumaria:", err);
      setError("Não foi possível carregar as configurações do portfólio de perfumaria.");
    } finally {
      setLoadingPortfolio(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeSubmenu === "portifolio-perfumaria" && !portfolioLoaded) {
      loadPortfolioSettings();
    }
  }, [activeSubmenu, loadPortfolioSettings, portfolioLoaded]);

  const loadPresentationsSettings = React.useCallback(async () => {
    try {
      setLoadingPresentations(true);
      setError("");
      const response = await Api.get("/admin/presentations");
      const settings = response?.data?.settings || {};
      setPresentations(getPresentationsFromSettings(settings));
      setPresentationsUpdatedAt(settings.updatedAt || "");
      setPresentationsLoaded(true);
    } catch (err) {
      console.error("Erro ao carregar configurações das apresentações:", err);
      setError("Não foi possível carregar as configurações das apresentações.");
    } finally {
      setLoadingPresentations(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeSubmenu === "apresentacoes" && !presentationsLoaded) {
      loadPresentationsSettings();
    }
  }, [activeSubmenu, loadPresentationsSettings, presentationsLoaded]);

  const loadMachineVideos = React.useCallback(async () => {
    try {
      setLoadingMachineVideos(true);
      setError("");
      const response = await Api.get("/admin/machine-videos");
      setMachineVideos(Array.isArray(response?.data?.items) ? response.data.items : []);
      setMachineVideosUpdatedAt(response?.data?.updatedAt || "");
      setMachineVideosLoaded(true);
    } catch (err) {
      console.error("Erro ao carregar vídeos de máquinas:", err);
      setError("Não foi possível carregar os vídeos de máquinas.");
    } finally {
      setLoadingMachineVideos(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeSubmenu === "videos-maquinas" && !machineVideosLoaded) {
      loadMachineVideos();
    }
  }, [activeSubmenu, loadMachineVideos, machineVideosLoaded]);

  const handleSelectFiles = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
    setError("");
    setSuccess("");
  };

  const handleSelectMobileFiles = (event) => {
    setSelectedMobileFiles(Array.from(event.target.files || []));
    setError("");
    setSuccess("");
  };

  const handleSelectMachineVideoFile = (event) => {
    const [file] = Array.from(event.target.files || []);
    setSelectedMachineVideoFile(file || null);
    setError("");
    setSuccess("");
  };

  const handleUpload = async (event) => {
    if (!bannerApiReady) { event.preventDefault(); return; }
    event.preventDefault();

    if (selectedFiles.length === 0) {
      setError("Selecione ao menos uma imagem para enviar.");
      return;
    }

    if (
      supportsMobileBanner &&
      selectedMobileFiles.length > 0 &&
      selectedMobileFiles.length !== selectedFiles.length
    ) {
      setError(
        "Quando houver versoes mobile, envie a mesma quantidade e na mesma ordem das imagens principais."
      );
      return;
    }

    const formData = new FormData();
    formData.append("portal", bannerConfig.portal);
    selectedFiles.forEach((file) => formData.append("images", file));
    selectedMobileFiles.forEach((file) => formData.append("mobileImages", file));

    try {
      setUploading(true);
      setError("");
      setSuccess("");

      const response = await Api.post(`/admin/login-banners?portal=${bannerConfig.portal}`, formData, {
        timeout: 30000,
      });
      setBanners(Array.isArray(response?.data?.items) ? response.data.items : []);
      setTransitionDurationMs(String(response?.data?.transitionDurationMs || transitionDurationMs));
      setSelectedFiles([]);
      setSelectedMobileFiles([]);
      setSuccess(`Banners adicionados com sucesso em ${bannerConfig.title}.`);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (mobileFileInputRef.current) {
        mobileFileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Erro ao enviar banners:", err);
      setError(
        err?.code === "ECONNABORTED"
          ? "O envio demorou mais do que o esperado. Tente novamente."
          : err?.response?.data?.message || "Não foi possível enviar os banners."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleUploadMachineVideo = async (event) => {
    event.preventDefault();

    if (!String(machineVideoTitle || "").trim()) {
      setError("Informe o nome do vídeo.");
      setSuccess("");
      return;
    }

    if (!String(machineVideoLink || "").trim() && !selectedMachineVideoFile) {
      setError("Informe um link do YouTube ou envie um vídeo.");
      setSuccess("");
      return;
    }

    if (!machineVideoLicensedEnabled && !machineVideoRealgemsEnabled) {
      setError("Selecione ao menos um portal para habilitar o vídeo.");
      setSuccess("");
      return;
    }

    const formData = new FormData();
    formData.append("title", machineVideoTitle);
    formData.append("linkUrl", machineVideoLink);
    formData.append("aspectRatioMode", machineVideoAspectRatio);
    formData.append("licensedEnabled", machineVideoLicensedEnabled ? "true" : "false");
    formData.append("realgemsEnabled", machineVideoRealgemsEnabled ? "true" : "false");
    if (selectedMachineVideoFile) {
      formData.append("video", selectedMachineVideoFile);
    }

    try {
      setUploadingMachineVideo(true);
      setError("");
      setSuccess("");

      const response = await Api.post("/admin/machine-videos", formData, {
        timeout: 600000,
      });

      setMachineVideos(Array.isArray(response?.data?.items) ? response.data.items : []);
      setMachineVideosUpdatedAt(response?.data?.updatedAt || "");
      setMachineVideosLoaded(true);
      setMachineVideoTitle("");
      setMachineVideoLink("");
      setMachineVideoAspectRatio("AUTO");
      setMachineVideoLicensedEnabled(true);
      setMachineVideoRealgemsEnabled(true);
      setSelectedMachineVideoFile(null);
      setSuccess("Vídeo cadastrado com sucesso.");

      if (machineVideoInputRef.current) {
        machineVideoInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Erro ao enviar vídeo:", err);
      setError(
        err?.code === "ECONNABORTED"
          ? "O upload demorou mais do que o esperado. Tente novamente."
          : err?.response?.data?.message || "Não foi possível enviar o vídeo."
      );
    } finally {
      setUploadingMachineVideo(false);
    }
  };

  const handleSaveSettings = async (event) => {
    if (!bannerApiReady) { event.preventDefault(); return; }
    event.preventDefault();

    const duration = Number(transitionDurationMs);

    if (!Number.isFinite(duration)) {
      setError("Informe um tempo de transição válido em milissegundos.");
      setSuccess("");
      return;
    }

    try {
      setSavingSettings(true);
      setError("");
      setSuccess("");

      const response = await Api.put("/admin/login-banners/settings", {
        portal: bannerConfig.portal,
        transitionDurationMs: duration,
      });

      setTransitionDurationMs(String(response?.data?.transitionDurationMs || duration));
      setSuccess(`Tempo de transi??o de ${bannerConfig.title} atualizado com sucesso.`);
    } catch (err) {
      console.error(`Erro ao salvar configurações de ${bannerConfig.title}:`, err);
      setError(
        err?.response?.data?.message ||
          `Não foi possível salvar o tempo de transição de ${bannerConfig.title}.`
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const handlePointsChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;

    setPointsForm((current) => ({
      ...current,
      [field]: value,
    }));
    setError("");
    setSuccess("");
  };

  const handleSavePointsSettings = async (event) => {
    event.preventDefault();

    try {
      setSavingPoints(true);
      setError("");
      setSuccess("");

      const response = await Api.put("/admin/points-settings", {
        pointsPerReal: pointsForm.pointsPerReal,
        campaignEnabled: pointsForm.campaignEnabled,
        campaignPointsPerReal: pointsForm.campaignPointsPerReal,
        campaignStartAt: pointsForm.campaignStartAt,
        campaignEndAt: pointsForm.campaignEndAt,
      });

      const settings = response?.data?.settings || {};
      setPointsForm(getPointsFormFromSettings(settings));
      setPointsUpdatedAt(settings.updatedAt || "");
      setSuccess("Configurações de pontos salvas com sucesso.");
    } catch (err) {
      console.error("Erro ao salvar configurações de pontos:", err);
      setError(err?.response?.data?.message || "Não foi possível salvar os pontos.");
    } finally {
      setSavingPoints(false);
    }
  };

  const handleSaveManualSettings = async (event) => {
    event.preventDefault();

    if (!String(manualEmbedCode || "").trim()) {
      setError("Cole o código de incorporação ou a URL do manual.");
      setSuccess("");
      return;
    }

    try {
      setSavingManual(true);
      setError("");
      setSuccess("");

      const response = await Api.put("/admin/manual-operations", {
        embedCode: manualEmbedCode,
      });

      const settings = response?.data?.settings || {};
      setManualEmbedCode(String(settings.embedCode || ""));
      setManualEmbedUrl(String(settings.embedUrl || ""));
      setManualUpdatedAt(settings.updatedAt || "");
      setManualLoaded(true);
      setSuccess("Manual de operações salvo com sucesso.");
    } catch (err) {
      console.error("Erro ao salvar configurações do manual de operações:", err);
      setError(
        err?.response?.data?.message ||
          "Não foi possível salvar as configurações do manual de operações."
      );
    } finally {
      setSavingManual(false);
    }
  };

  const handleSavePortfolioSettings = async (event) => {
    event.preventDefault();

    if (!String(portfolioEmbedCode || "").trim()) {
      setError("Cole o código de incorporação ou a URL do portfólio.");
      setSuccess("");
      return;
    }

    try {
      setSavingPortfolio(true);
      setError("");
      setSuccess("");

      const response = await Api.put("/admin/perfumery-portfolio", {
        embedCode: portfolioEmbedCode,
      });

      const settings = response?.data?.settings || {};
      setPortfolioEmbedCode(String(settings.embedCode || ""));
      setPortfolioEmbedUrl(String(settings.embedUrl || ""));
      setPortfolioUpdatedAt(settings.updatedAt || "");
      setPortfolioLoaded(true);
      setSuccess("Portfólio de perfumaria salvo com sucesso.");
    } catch (err) {
      console.error("Erro ao salvar configurações do portfólio de perfumaria:", err);
      setError(
        err?.response?.data?.message ||
          "Não foi possível salvar as configurações do portfólio de perfumaria."
      );
    } finally {
      setSavingPortfolio(false);
    }
  };

  const handleSavePresentationsSettings = async (event) => {
    event.preventDefault();

    const invalidIndex = presentations.findIndex(
      (item) =>
        !String(item.title || "").trim() ||
        !extractPresentationEmbedUrl(item.embedCode || item.embedUrl)
    );

    if (invalidIndex !== -1) {
      setError(
        `Preencha o título e uma URL/iframe válido na apresentação ${invalidIndex + 1}.`
      );
      setSuccess("");
      return;
    }

    try {
      setSavingPresentations(true);
      setError("");
      setSuccess("");

      const response = await Api.put("/admin/presentations", {
        items: presentations.map((item, index) => ({
          id: item.id,
          title: String(item.title || "").trim(),
          embedCode: String(item.embedCode || "").trim(),
          audience: item.audience,
          position: index + 1,
        })),
      });

      const settings = response?.data?.settings || {};
      setPresentations(getPresentationsFromSettings(settings));
      setPresentationsUpdatedAt(settings.updatedAt || "");
      setPresentationsLoaded(true);
      setSuccess("Apresentações salvas com sucesso.");
    } catch (err) {
      console.error("Erro ao salvar configurações das apresentações:", err);
      setError(
        err?.response?.data?.message ||
          "Não foi possível salvar as configurações das apresentações."
      );
    } finally {
      setSavingPresentations(false);
    }
  };

  const handleAddPresentation = () => {
    setPresentations((current) => [...current, createPresentationDraft()]);
    setError("");
    setSuccess("");
  };

  const handleUpdatePresentation = (presentationId, field, value) => {
    setPresentations((current) =>
      current.map((item) =>
        item.id === presentationId
          ? {
              ...item,
              [field]: value,
              ...(field === "embedCode"
                ? { embedUrl: extractPresentationEmbedUrl(value) }
                : {}),
            }
          : item
      )
    );
    setSuccess("");
  };

  const handleRemovePresentation = (presentationId) => {
    setPresentations((current) => current.filter((item) => item.id !== presentationId));
    setError("");
    setSuccess("");
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!bannerApiReady) return;
    if (!window.confirm("Deseja remover este banner do carrossel?")) return;

    try {
      setDeletingBannerId(bannerId);
      setError("");
      setSuccess("");

      await Api.delete(`/admin/login-banners/${bannerId}`, { params: { portal: bannerConfig.portal } });
      setBanners((current) =>
        current
          .filter((item) => item.id !== bannerId)
          .map((item, index) => ({
            ...item,
            position: index + 1,
          }))
      );
      setSuccess("Banner removido com sucesso.");
    } catch (err) {
      console.error("Erro ao remover banner:", err);
      setError(err?.response?.data?.message || "Não foi possível remover o banner.");
    } finally {
      setDeletingBannerId("");
    }
  };

  const handleBannerDragStart = (bannerId) => {
    setDraggedBannerId(bannerId);
    setError("");
    setSuccess("");
  };

  const handleBannerDrop = async (targetId) => {
    if (!bannerApiReady) return;
    if (!draggedBannerId) return;

    const previousItems = banners;
    const nextItems = moveItemById(previousItems, draggedBannerId, targetId);

    setDraggedBannerId("");

    if (nextItems === previousItems) {
      return;
    }

    try {
      setReorderingBanners(true);
      setBanners(nextItems);
      setError("");
      setSuccess("");

      const response = await Api.put("/admin/login-banners/reorder", {
        portal: bannerConfig.portal,
        orderedIds: nextItems.map((item) => item.id),
      });

      setBanners(Array.isArray(response?.data?.items) ? response.data.items : nextItems);
      setTransitionDurationMs(String(response?.data?.transitionDurationMs || transitionDurationMs));
      setSuccess("Sequência dos banners atualizada com sucesso.");
    } catch (err) {
      console.error("Erro ao reordenar banners:", err);
      setBanners(previousItems);
      setError(
        err?.response?.data?.message || "Não foi possível atualizar a sequência dos banners."
      );
    } finally {
      setReorderingBanners(false);
    }
  };

  const handleStartEditMachineVideo = (video) => {
    setEditingMachineVideoId(video.id);
    setEditingMachineVideoTitle(video.title || "");
    setEditingMachineVideoLink(video.linkUrl || "");
    setEditingMachineVideoAspectRatio(video.aspectRatioMode || video.aspectRatio || "AUTO");
    setEditingMachineVideoLicensedEnabled(Boolean(video.licensedEnabled));
    setEditingMachineVideoRealgemsEnabled(Boolean(video.realgemsEnabled));
    setError("");
    setSuccess("");
  };

  const handleCancelEditMachineVideo = () => {
    setEditingMachineVideoId("");
    setEditingMachineVideoTitle("");
    setEditingMachineVideoLink("");
    setEditingMachineVideoAspectRatio("AUTO");
    setEditingMachineVideoLicensedEnabled(true);
    setEditingMachineVideoRealgemsEnabled(true);
  };

  const handleSaveMachineVideo = async (videoId) => {
    if (!String(editingMachineVideoTitle || "").trim()) {
      setError("Informe o nome do vídeo.");
      setSuccess("");
      return;
    }

    if (!editingMachineVideoLicensedEnabled && !editingMachineVideoRealgemsEnabled) {
      setError("Selecione ao menos um portal para habilitar o vídeo.");
      setSuccess("");
      return;
    }

    try {
      setSavingMachineVideoId(videoId);
      setError("");
      setSuccess("");

      const response = await Api.put(`/admin/machine-videos/${videoId}`, {
        title: editingMachineVideoTitle,
        linkUrl: editingMachineVideoLink,
        aspectRatioMode: editingMachineVideoAspectRatio,
        licensedEnabled: editingMachineVideoLicensedEnabled,
        realgemsEnabled: editingMachineVideoRealgemsEnabled,
      });

      const updatedItem = response?.data?.item;
      setMachineVideos((current) =>
        current.map((item) => (item.id === videoId ? { ...item, ...updatedItem } : item))
      );
      setEditingMachineVideoId("");
      setEditingMachineVideoTitle("");
      setEditingMachineVideoLink("");
      setEditingMachineVideoAspectRatio("AUTO");
      setEditingMachineVideoLicensedEnabled(true);
      setEditingMachineVideoRealgemsEnabled(true);
      setSuccess("Vídeo atualizado com sucesso.");
    } catch (err) {
      console.error("Erro ao atualizar vídeo:", err);
      setError(err?.response?.data?.message || "Não foi possível atualizar o vídeo.");
    } finally {
      setSavingMachineVideoId("");
    }
  };

  const handleDeleteMachineVideo = async (videoId) => {
    if (!window.confirm("Deseja remover este vídeo da biblioteca?")) return;

    try {
      setDeletingMachineVideoId(videoId);
      setError("");
      setSuccess("");

      await Api.delete(`/admin/machine-videos/${videoId}`);
      setMachineVideos((current) =>
        current
          .filter((item) => item.id !== videoId)
          .map((item, index) => ({
            ...item,
            position: index + 1,
          }))
      );
      setSuccess("Vídeo removido com sucesso.");
    } catch (err) {
      console.error("Erro ao remover vídeo:", err);
      setError(err?.response?.data?.message || "Não foi possível remover o vídeo.");
    } finally {
      setDeletingMachineVideoId("");
    }
  };

  const handleMachineVideoDragStart = (videoId) => {
    setDraggedMachineVideoId(videoId);
    setError("");
    setSuccess("");
  };

  const handleMachineVideoDrop = async (targetId) => {
    if (!draggedMachineVideoId) return;

    const previousItems = machineVideos;
    const nextItems = moveItemById(previousItems, draggedMachineVideoId, targetId);

    setDraggedMachineVideoId("");

    if (nextItems === previousItems) {
      return;
    }

    try {
      setReorderingMachineVideos(true);
      setMachineVideos(nextItems);
      setError("");
      setSuccess("");

      const response = await Api.put("/admin/machine-videos/reorder", {
        orderedIds: nextItems.map((item) => item.id),
      });

      setMachineVideos(Array.isArray(response?.data?.items) ? response.data.items : nextItems);
      setMachineVideosUpdatedAt(response?.data?.updatedAt || "");
      setSuccess("Sequência dos vídeos atualizada com sucesso.");
    } catch (err) {
      console.error("Erro ao reordenar vídeos:", err);
      setMachineVideos(previousItems);
      setError(
        err?.response?.data?.message || "Não foi possível atualizar a sequência dos vídeos."
      );
    } finally {
      setReorderingMachineVideos(false);
    }
  };

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} settingsActive="active" />

      <main className="main-content">
        <Topbar
          page={isBannerSubmenu ? bannerConfig.pageTitle : getSettingsPageTitle(activeSubmenu)}
          onToggleMenu={toggleMenu}
        />

        <section className="admin-settings-grid">
          {false ? <article className="admin-settings-panel">
            <div className="admin-settings-header">
              <div>
                <h2>Submenu das configurações</h2>
                <p>Organize os ajustes do super administrador por área do sistema.</p>
              </div>
              <span className="admin-settings-badge">Super administrador</span>
            </div>

            <div className="admin-settings-submenu">
              <button
                type="button"
                className={activeSubmenu === "banner-inicial" ? "is-active" : ""}
                onClick={() => handleSubmenuChange("banner-inicial")}
              >
                Banner inicial
              </button>
              <button
                type="button"
                className={activeSubmenu === "pontos" ? "is-active" : ""}
                onClick={() => handleSubmenuChange("pontos")}
              >
                Pontos
              </button>
              <button
                type="button"
                className={activeSubmenu === "manual-operacao" ? "is-active" : ""}
                onClick={() => handleSubmenuChange("manual-operacao")}
              >
                Manual de operações
              </button>
              <button
                type="button"
                className={activeSubmenu === "portifolio-perfumaria" ? "is-active" : ""}
                onClick={() => handleSubmenuChange("portifolio-perfumaria")}
              >
                Portfólio de perfumaria
              </button>
              <button
                type="button"
                className={activeSubmenu === "videos-maquinas" ? "is-active" : ""}
                onClick={() => handleSubmenuChange("videos-maquinas")}
              >
                Vídeos de máquinas
              </button>
            </div>
          </article> : null}

          {isBannerSubmenu ? (
            <>
              <article className="admin-settings-panel">
                <div className="admin-settings-header">
                  <div>
                    <h2>{bannerConfig.settingsTitle}</h2>
                    <p>{bannerConfig.settingsDescription}</p>
                  </div>
                </div>

                <div className="admin-settings-split">
                  <form className="admin-settings-card" onSubmit={handleUpload}>
                    <div className="admin-settings-card-header">
                      <h3>Imagens do carrossel</h3>
                      <p>
                        Envie novos banners quando precisar. Eles ficam salvos no servidor e podem
                        ser reordenados abaixo.
                      </p>
                    </div>

                    <label className="admin-upload-box" htmlFor="login-banners">
                      <span>Selecionar imagens</span>
                      <small>Formatos aceitos: JPG, PNG, WEBP. Limite de 10 MB por arquivo.</small>
                    </label>

                    <input
                      ref={fileInputRef}
                      id="login-banners"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleSelectFiles}
                    />

                    {selectedFiles.length > 0 ? (
                      <div className="admin-selected-files">
                        {selectedFiles.map((file) => (
                          <div
                            className="admin-selected-file"
                            key={`${file.name}-${file.lastModified}`}
                          >
                            <strong>{file.name}</strong>
                            <span>{formatFileSize(file.size)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="admin-settings-help">
                        Você pode subir mais banners a qualquer momento. A sequência final é
                        organizada na lista abaixo com clicar e arrastar.
                      </p>
                    )}

                    {supportsMobileBanner ? (
                      <>
                        <div className="admin-machine-video-upload-optional">
                          <div className="admin-machine-video-upload-optional-header">
                            <strong>Versoes mobile</strong>
                            <small>
                              Opcional. Se enviar, selecione a mesma quantidade e ordem das imagens
                              principais.
                            </small>
                          </div>

                          <label
                            className="admin-upload-box admin-upload-box-compact"
                            htmlFor="login-banners-mobile"
                          >
                            <span>Selecionar imagens mobile</span>
                            <small>
                              Use aqui os banners exclusivos para celular. Se ficar vazio, o sistema
                              reutiliza a imagem principal.
                            </small>
                          </label>

                          <input
                            ref={mobileFileInputRef}
                            id="login-banners-mobile"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleSelectMobileFiles}
                          />

                          {selectedMobileFiles.length > 0 ? (
                            <div className="admin-selected-files">
                              {selectedMobileFiles.map((file) => (
                                <div
                                  className="admin-selected-file"
                                  key={`mobile-${file.name}-${file.lastModified}`}
                                >
                                  <strong>{file.name}</strong>
                                  <span>{formatFileSize(file.size)}</span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <p className="admin-settings-help">
                          Cada banner pode ter uma versão mobile separada. No login deste portal,
                          ela sera usada automaticamente em telas menores.
                        </p>
                      </>
                    ) : null}

                    <div className="admin-settings-actions">
                      <button type="submit" disabled={!bannerApiReady || uploading || selectedFiles.length === 0}>
                        {uploading ? "Enviando..." : "Adicionar banners"}
                      </button>
                    </div>
                  </form>

                  <form className="admin-settings-card" onSubmit={handleSaveSettings}>
                    <div className="admin-settings-card-header">
                      <h3>Tempo de transição</h3>
                      <p>Defina em milissegundos quanto tempo cada slide fica visível.</p>
                    </div>

                    <label className="admin-settings-field" htmlFor="transition-duration-ms">
                      <span>Duração do slide em milissegundos</span>
                      <input
                        id="transition-duration-ms"
                        type="number"
                        min="500"
                        step="100"
                        value={transitionDurationMs}
                        onChange={(event) => setTransitionDurationMs(event.target.value)}
                        placeholder="5000"
                      />
                      <small>Valor mínimo de 500 ms e máximo de 60000 ms.</small>
                    </label>

                    <div className="admin-settings-actions">
                      <button type="submit" disabled={!bannerApiReady || savingSettings}>
                        {savingSettings ? "Salvando..." : "Salvar tempo de transição"}
                      </button>
                    </div>
                  </form>
                </div>

                {error ? <div className="admin-settings-message error">{error}</div> : null}
                {success ? <div className="admin-settings-message success">{success}</div> : null}
              </article>

              <article className="admin-settings-panel">
                <div className="admin-settings-header compact">
                  <div>
                    <h2>{`Banners cadastrados - ${bannerConfig.title}`}</h2>
                    <p>
                      {loading
                        ? "Carregando imagens..."
                        : `${banners.length} banner(s) ativo(s) em ${bannerConfig.countLabel}`}
                    </p>
                  </div>
                </div>

                {loading ? (
                  <div className="admin-settings-empty">Carregando banners...</div>
                ) : banners.length === 0 ? (
                  <div className="admin-settings-empty">
                    {bannerConfig.emptyLabel}
                  </div>
                ) : (
                  <div className="admin-banner-table">
                    <div className="admin-banner-head">
                      <span>Ordem</span>
                      <span>Preview</span>
                      <span>Arquivo</span>
                      <span>Tamanho</span>
                      <span>Ações</span>
                    </div>

                    <div className="admin-banner-body-list">
                      {banners.map((banner, index) => {
                        const isDragging = draggedBannerId === banner.id;

                        return (
                          <div
                            key={banner.id}
                            className={`admin-banner-row${isDragging ? " is-dragging" : ""}`}
                            draggable
                            onDragStart={() => handleBannerDragStart(banner.id)}
                            onDragEnd={() => setDraggedBannerId("")}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={() => handleBannerDrop(banner.id)}
                          >
                            <div className="admin-banner-order">
                              <span className="admin-banner-drag">::</span>
                              <strong>{index + 1}</strong>
                            </div>

                            <div className="admin-banner-preview-tile">
                              <img src={banner.url} alt={banner.originalName || `Banner ${index + 1}`} />
                              {banner.mobileUrl ? (
                                <span className="admin-banner-mobile-chip">Mobile</span>
                              ) : null}
                            </div>

                            <div className="admin-banner-meta">
                              <strong>{banner.originalName || `Banner ${index + 1}`}</strong>
                              <small>Cadastrado em {formatDateTime(banner.createdAt)}</small>
                              {banner.mobileUrl ? (
                                <small>
                                  Mobile: {banner.mobileOriginalName || "imagem dedicada enviada"}
                                </small>
                              ) : supportsMobileBanner ? (
                                <small>Mobile: usando a imagem principal</small>
                              ) : null}
                            </div>

                            <div className="admin-banner-size">
                              <strong>{formatFileSize(banner.size)}</strong>
                              <small>
                                {banner.mobileUrl
                                  ? `Mobile ${formatFileSize(banner.mobileSize)}`
                                  : reorderingBanners
                                    ? "Salvando ordem..."
                                    : "Arraste para ordenar"}
                              </small>
                            </div>

                            <div className="admin-banner-actions">
                              <button
                                type="button"
                                className="admin-banner-remove"
                                onClick={() => handleDeleteBanner(banner.id)}
                                disabled={Boolean(deletingBannerId) || reorderingBanners}
                              >
                                {deletingBannerId === banner.id ? "Removendo..." : "Remover"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </article>
            </>
          ) : null}

          {activeSubmenu === "pontos" ? (
            <article className="admin-settings-panel">
              <div className="admin-settings-header">
                <div>
                  <h2>Sistema de pontos</h2>
                  <p>
                    Defina a regra padrão de pontos por real e uma campanha temporária com
                    pontuação especial.
                  </p>
                </div>
                <span className="admin-settings-badge">Super administrador</span>
              </div>

              {loadingPoints ? (
                <div className="admin-settings-empty">Carregando configurações de pontos...</div>
              ) : (
                <form className="admin-settings-points-form" onSubmit={handleSavePointsSettings}>
                  <div className="admin-settings-split">
                    <section className="admin-settings-card">
                      <div className="admin-settings-card-header">
                        <h3>Regra padrão</h3>
                        <p>Exemplo: com 1 ponto por real, R$ 100,50 gera 100 pontos.</p>
                      </div>

                      <label className="admin-settings-field" htmlFor="points-per-real">
                        <span>Pontos para cada R$ 1,00</span>
                        <input
                          id="points-per-real"
                          type="number"
                          min="0"
                          step="0.01"
                          value={pointsForm.pointsPerReal}
                          onChange={handlePointsChange("pointsPerReal")}
                          placeholder="1"
                        />
                        <small>O sistema sempre arredonda os pontos para baixo.</small>
                      </label>
                    </section>

                    <section className="admin-settings-card">
                      <div className="admin-settings-card-header">
                        <h3>Campanha de pontos</h3>
                        <p>Use para datas especiais, como R$ 1,00 valendo 2 pontos.</p>
                      </div>

                      <label className="admin-settings-checkbox">
                        <input
                          type="checkbox"
                          checked={pointsForm.campaignEnabled}
                          onChange={handlePointsChange("campaignEnabled")}
                        />
                        <span>Ativar campanha</span>
                      </label>

                      <label className="admin-settings-field" htmlFor="campaign-points-per-real">
                        <span>Pontos da campanha para cada R$ 1,00</span>
                        <input
                          id="campaign-points-per-real"
                          type="number"
                          min="0"
                          step="0.01"
                          value={pointsForm.campaignPointsPerReal}
                          onChange={handlePointsChange("campaignPointsPerReal")}
                          placeholder="2"
                          disabled={!pointsForm.campaignEnabled}
                        />
                      </label>

                      <div className="admin-settings-date-grid">
                        <label className="admin-settings-field" htmlFor="campaign-start-at">
                          <span>Início</span>
                          <input
                            id="campaign-start-at"
                            type="date"
                            value={pointsForm.campaignStartAt}
                            onChange={handlePointsChange("campaignStartAt")}
                            disabled={!pointsForm.campaignEnabled}
                          />
                        </label>

                        <label className="admin-settings-field" htmlFor="campaign-end-at">
                          <span>Fim</span>
                          <input
                            id="campaign-end-at"
                            type="date"
                            value={pointsForm.campaignEndAt}
                            onChange={handlePointsChange("campaignEndAt")}
                            disabled={!pointsForm.campaignEnabled}
                          />
                        </label>
                      </div>
                    </section>
                  </div>

                  <div className="admin-settings-points-summary">
                    <strong>Como será calculado</strong>
                    <span>
                      R$ 100,50 em comissão = {Math.floor(100.5 * Number(pointsForm.pointsPerReal || 0))}{" "}
                      pontos na regra padrão.
                    </span>
                    {pointsForm.campaignEnabled ? (
                      <span>
                        Na campanha ={" "}
                        {Math.floor(100.5 * Number(pointsForm.campaignPointsPerReal || 0))} pontos.
                      </span>
                    ) : null}
                    {pointsUpdatedAt ? (
                      <small>Última atualização: {String(pointsUpdatedAt)}</small>
                    ) : null}
                  </div>

                  <div className="admin-settings-actions">
                    <button type="submit" disabled={savingPoints}>
                      {savingPoints ? "Salvando..." : "Salvar configurações de pontos"}
                    </button>
                  </div>

                  {error ? <div className="admin-settings-message error">{error}</div> : null}
                  {success ? <div className="admin-settings-message success">{success}</div> : null}
                </form>
              )}
            </article>
          ) : null}

          {activeSubmenu === "manual-operacao" ? (
            <article className="admin-settings-panel">
              <div className="admin-settings-header">
                <div>
                  <h2>Manual de operações</h2>
                  <p>
                    Cole abaixo o código de incorporação do manual. O mesmo conteúdo será exibido
                    para os licenciados na área de documentos.
                  </p>
                </div>
                <span className="admin-settings-badge">Super administrador</span>
              </div>

              {loadingManual ? (
                <div className="admin-settings-empty">
                  Carregando configurações do manual de operações...
                </div>
              ) : (
                <form className="admin-settings-points-form" onSubmit={handleSaveManualSettings}>
                  <section className="admin-settings-card">
                    <div className="admin-settings-card-header">
                      <h3>Código de incorporação</h3>
                      <p>Aceita URL direta ou código de iframe. Exemplo: embed do FlipHTML5.</p>
                    </div>

                    <label className="admin-settings-field" htmlFor="manual-embed-code">
                      <span>Cole o código de incorporação</span>
                      <textarea
                        id="manual-embed-code"
                        value={manualEmbedCode}
                        onChange={(event) => setManualEmbedCode(event.target.value)}
                        placeholder='<iframe src="https://..." title="Manual"></iframe>'
                        rows={7}
                      />
                      <small>
                        O sistema usa o `src` do iframe, ou a própria URL, para montar a
                        visualização do manual.
                      </small>
                    </label>

                    <div className="admin-settings-actions">
                      <button type="submit" disabled={savingManual}>
                        {savingManual ? "Salvando..." : "Salvar manual de operações"}
                      </button>
                    </div>
                  </section>

                  {error ? <div className="admin-settings-message error">{error}</div> : null}
                  {success ? <div className="admin-settings-message success">{success}</div> : null}

                  <section className="admin-settings-card">
                    <div className="admin-settings-card-header">
                      <h3>Visualização</h3>
                      <p>Confira abaixo exatamente o manual que será exibido ao licenciado.</p>
                    </div>

                    {manualUpdatedAt ? (
                      <p className="admin-settings-help">
                        Última atualização: {String(manualUpdatedAt)}
                      </p>
                    ) : null}

                    {manualEmbedUrl ? (
                      <div className="admin-settings-manual-preview">
                        <iframe
                          className="admin-settings-manual-frame"
                          src={manualEmbedUrl}
                          title="Preview do manual de operações"
                          scrolling="no"
                          frameBorder="0"
                          allowTransparency
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="admin-settings-empty">
                        Salve um código de incorporação válido para visualizar o manual aqui.
                      </div>
                    )}
                  </section>
                </form>
              )}
            </article>
          ) : null}

          {activeSubmenu === "portifolio-perfumaria" ? (
            <article className="admin-settings-panel">
              <div className="admin-settings-header">
                <div>
                  <h2>Portfólio de perfumaria</h2>
                  <p>
                    Cole abaixo o código de incorporação do portfólio. O mesmo conteúdo será
                    exibido para os licenciados na área de documentos.
                  </p>
                </div>
                <span className="admin-settings-badge">Super administrador</span>
              </div>

              {loadingPortfolio ? (
                <div className="admin-settings-empty">
                  Carregando configurações do portfólio de perfumaria...
                </div>
              ) : (
                <form className="admin-settings-points-form" onSubmit={handleSavePortfolioSettings}>
                  <section className="admin-settings-card">
                    <div className="admin-settings-card-header">
                      <h3>Código de incorporação</h3>
                      <p>Aceita URL direta ou código de iframe. Exemplo: embed de revista digital.</p>
                    </div>

                    <label className="admin-settings-field" htmlFor="portfolio-embed-code">
                      <span>Cole o código de incorporação</span>
                      <textarea
                        id="portfolio-embed-code"
                        value={portfolioEmbedCode}
                        onChange={(event) => setPortfolioEmbedCode(event.target.value)}
                        placeholder='<iframe src="https://..." title="Portfólio"></iframe>'
                        rows={7}
                      />
                      <small>
                        O sistema usa o `src` do iframe, ou a própria URL, para montar a
                        visualização do portfólio.
                      </small>
                    </label>

                    <div className="admin-settings-actions">
                      <button type="submit" disabled={savingPortfolio}>
                        {savingPortfolio ? "Salvando..." : "Salvar portfólio de perfumaria"}
                      </button>
                    </div>
                  </section>

                  {error ? <div className="admin-settings-message error">{error}</div> : null}
                  {success ? <div className="admin-settings-message success">{success}</div> : null}

                  <section className="admin-settings-card">
                    <div className="admin-settings-card-header">
                      <h3>Visualização</h3>
                      <p>Confira abaixo exatamente o portfólio que será exibido ao licenciado.</p>
                    </div>

                    {portfolioUpdatedAt ? (
                      <p className="admin-settings-help">
                        Última atualização: {String(portfolioUpdatedAt)}
                      </p>
                    ) : null}

                    {portfolioEmbedUrl ? (
                      <div className="admin-settings-manual-preview">
                        <iframe
                          className="admin-settings-manual-frame"
                          src={portfolioEmbedUrl}
                          title="Preview do portfólio de perfumaria"
                          scrolling="no"
                          frameBorder="0"
                          allowTransparency
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="admin-settings-empty">
                        Salve um código de incorporação válido para visualizar o portfólio aqui.
                      </div>
                    )}
                  </section>
                </form>
              )}
            </article>
          ) : null}

          {activeSubmenu === "apresentacoes" ? (
            <article className="admin-settings-panel">
              <div className="admin-settings-header">
                <div>
                  <h2>Apresentações</h2>
                  <p>
                    Cadastre quantas apresentações precisar e escolha se cada item será exibido
                    para licenciados, usuários Realgems ou nos dois painéis.
                  </p>
                </div>
                <span className="admin-settings-badge">Super administrador</span>
              </div>

              {loadingPresentations ? (
                <div className="admin-settings-empty">
                  Carregando configurações das apresentações...
                </div>
              ) : (
                <form
                  className="admin-settings-points-form admin-presentations-form"
                  onSubmit={handleSavePresentationsSettings}
                >
                  <section className="admin-settings-card admin-presentations-management">
                    <div className="admin-presentations-toolbar">
                      <div className="admin-settings-card-header">
                        <h3>Apresentações cadastradas</h3>
                        <p>
                          Use uma URL direta ou o código completo do iframe em cada apresentação.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="admin-presentation-add"
                        onClick={handleAddPresentation}
                      >
                        + Adicionar apresentação
                      </button>
                    </div>

                    {presentations.length ? (
                      <div className="admin-presentations-list">
                        {presentations.map((presentation, index) => {
                          const previewUrl = extractPresentationEmbedUrl(
                            presentation.embedCode || presentation.embedUrl
                          );

                          return (
                            <article className="admin-presentation-editor" key={presentation.id}>
                              <div className="admin-presentation-editor-header">
                                <div>
                                  <span className="admin-presentation-number">
                                    Apresentação {index + 1}
                                  </span>
                                  <strong>{presentation.title || "Sem título"}</strong>
                                </div>
                                <button
                                  type="button"
                                  className="admin-presentation-remove"
                                  onClick={() => handleRemovePresentation(presentation.id)}
                                >
                                  Remover
                                </button>
                              </div>

                              <div className="admin-presentation-fields-grid">
                                <label
                                  className="admin-settings-field"
                                  htmlFor={`presentation-title-${presentation.id}`}
                                >
                                  <span>Título</span>
                                  <input
                                    id={`presentation-title-${presentation.id}`}
                                    value={presentation.title}
                                    maxLength={160}
                                    onChange={(event) =>
                                      handleUpdatePresentation(
                                        presentation.id,
                                        "title",
                                        event.target.value
                                      )
                                    }
                                    placeholder="Ex: Apresentação comercial"
                                  />
                                </label>

                                <label
                                  className="admin-settings-field"
                                  htmlFor={`presentation-audience-${presentation.id}`}
                                >
                                  <span>Disponível em</span>
                                  <select
                                    id={`presentation-audience-${presentation.id}`}
                                    value={presentation.audience}
                                    onChange={(event) =>
                                      handleUpdatePresentation(
                                        presentation.id,
                                        "audience",
                                        event.target.value
                                      )
                                    }
                                  >
                                    {PRESENTATION_AUDIENCE_OPTIONS.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              </div>

                              <label
                                className="admin-settings-field"
                                htmlFor={`presentation-embed-${presentation.id}`}
                              >
                                <span>Código de incorporação ou URL</span>
                                <textarea
                                  id={`presentation-embed-${presentation.id}`}
                                  value={presentation.embedCode}
                                  onChange={(event) =>
                                    handleUpdatePresentation(
                                      presentation.id,
                                      "embedCode",
                                      event.target.value
                                    )
                                  }
                                  placeholder='<iframe src="https://..." title="Apresentação"></iframe>'
                                  rows={5}
                                />
                              </label>

                              {previewUrl ? (
                                <div className="admin-presentation-preview">
                                  <iframe
                                    src={previewUrl}
                                    title={`Preview de ${presentation.title || `apresentação ${index + 1}`}`}
                                    scrolling="no"
                                    frameBorder="0"
                                    allowTransparency
                                    allowFullScreen
                                  />
                                </div>
                              ) : (
                                <div className="admin-presentation-preview-empty">
                                  Informe uma URL ou iframe válido para exibir a prévia.
                                </div>
                              )}
                            </article>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="admin-settings-empty admin-presentations-empty">
                        Nenhuma apresentação cadastrada. Clique em “Adicionar apresentação”
                        para criar a primeira.
                      </div>
                    )}

                    <div className="admin-settings-actions">
                      <button type="submit" disabled={savingPresentations}>
                        {savingPresentations ? "Salvando..." : "Salvar todas as apresentações"}
                      </button>
                    </div>
                  </section>

                  {error ? <div className="admin-settings-message error">{error}</div> : null}
                  {success ? <div className="admin-settings-message success">{success}</div> : null}

                  {presentationsUpdatedAt ? (
                    <p className="admin-settings-help">
                      Última atualização: {formatDateTime(presentationsUpdatedAt)}
                    </p>
                  ) : null}
                </form>
              )}
            </article>
          ) : null}

          {activeSubmenu === "videos-maquinas" ? (
            <>
              <article className="admin-settings-panel">
                <div className="admin-settings-header">
                  <div>
                    <h2>Vídeos de máquinas</h2>
                    <p>
                      Cadastre os vídeos de apoio comercial e organize a sequência arrastando os
                      itens na tabela.
                    </p>
                  </div>
                  <span className="admin-settings-badge">Super administrador</span>
                </div>

                <div className="admin-settings-split admin-settings-split-wide">
                  <form className="admin-settings-card" onSubmit={handleUploadMachineVideo}>
                    <div className="admin-settings-card-header">
                      <h3>Novo vídeo</h3>
                      <p>Envie um vídeo por vez com o nome que deve aparecer na biblioteca.</p>
                    </div>

                    <label className="admin-settings-field" htmlFor="machine-video-title">
                      <span>Nome do vídeo</span>
                      <input
                        id="machine-video-title"
                        type="text"
                        value={machineVideoTitle}
                        onChange={(event) => setMachineVideoTitle(event.target.value)}
                        placeholder="Ex: Máquina Smart 300 - Apresentação"
                      />
                    </label>

                    <label className="admin-settings-field" htmlFor="machine-video-link">
                      <span>Link do YouTube</span>
                      <input
                        id="machine-video-link"
                        type="url"
                        value={machineVideoLink}
                        onChange={(event) => setMachineVideoLink(event.target.value)}
                        placeholder="https://www.youtube.com/shorts/dl23gYcbZFM"
                      />
                      <small>
                        Links de `shorts` costumam ser identificados automaticamente como 9:16.
                      </small>
                    </label>

                    <label className="admin-settings-field" htmlFor="machine-video-aspect-ratio">
                      <span>Formato do vídeo</span>
                      <select
                        id="machine-video-aspect-ratio"
                        value={machineVideoAspectRatio}
                        onChange={(event) => setMachineVideoAspectRatio(event.target.value)}
                      >
                        {MACHINE_VIDEO_ASPECT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div className="admin-settings-field">
                      <span>Onde habilitar este vídeo</span>
                      <div className="admin-audience-toggle-grid">
                        <label className="admin-audience-toggle">
                          <input
                            type="checkbox"
                            checked={machineVideoLicensedEnabled}
                            onChange={(event) =>
                              setMachineVideoLicensedEnabled(event.target.checked)
                            }
                          />
                          <span className="admin-audience-toggle-switch" aria-hidden="true" />
                          <span className="admin-audience-toggle-text">Licenciado</span>
                        </label>
                        <label className="admin-audience-toggle">
                          <input
                            type="checkbox"
                            checked={machineVideoRealgemsEnabled}
                            onChange={(event) =>
                              setMachineVideoRealgemsEnabled(event.target.checked)
                            }
                          />
                          <span className="admin-audience-toggle-switch" aria-hidden="true" />
                          <span className="admin-audience-toggle-text">Realgems</span>
                        </label>
                      </div>
                      <small>Você pode deixar ativo em um portal ou nos dois ao mesmo tempo.</small>
                    </div>

                    <div className="admin-machine-video-upload-optional">
                      <div className="admin-machine-video-upload-optional-header">
                        <strong>Upload manual</strong>
                        <small>
                          Opcional. Se houver link e arquivo, o link será priorizado.
                        </small>
                      </div>

                      <label
                        className="admin-upload-box admin-upload-box-compact"
                        htmlFor="machine-video-file"
                      >
                        <span>Selecionar vídeo</span>
                        <small>MP4, WEBM e similares. Limite de 200 MB.</small>
                      </label>
                    </div>

                    {false ? <label className="admin-upload-box" htmlFor="machine-video-file">
                      <span>Selecionar vídeo</span>
                      <small>Formatos aceitos: MP4, WEBM e similares. Limite de 200 MB.</small>
                    </label> : null}

                    <input
                      ref={machineVideoInputRef}
                      id="machine-video-file"
                      type="file"
                      accept="video/*"
                      onChange={handleSelectMachineVideoFile}
                    />

                    {selectedMachineVideoFile ? (
                      <div className="admin-selected-files">
                        <div className="admin-selected-file">
                          <strong>{selectedMachineVideoFile.name}</strong>
                          <span>{formatFileSize(selectedMachineVideoFile.size)}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="admin-settings-help">
                        O cadastro por link entra primeiro. O upload local é opcional para casos
                        específicos.
                      </p>
                    )}

                    <div className="admin-settings-actions">
                      <button
                        type="submit"
                        disabled={
                          uploadingMachineVideo ||
                          (!String(machineVideoLink || "").trim() && !selectedMachineVideoFile)
                        }
                      >
                        {uploadingMachineVideo ? "Enviando..." : "Enviar vídeo"}
                      </button>
                    </div>
                  </form>

                  <section className="admin-settings-card">
                    <div className="admin-settings-card-header">
                      <h3>Como organizar</h3>
                      <p>
                        Arraste cada linha para cima ou para baixo para definir a sequência final.
                      </p>
                    </div>

                    <div className="admin-settings-info-grid">
                      <div className="admin-settings-info-item">
                        <strong>{machineVideos.length}</strong>
                        <span>vídeo(s) cadastrados</span>
                      </div>
                      <div className="admin-settings-info-item">
                        <strong>{reorderingMachineVideos ? "Salvando..." : "Drag and drop"}</strong>
                        <span>ordenação rápida na tabela</span>
                      </div>
                    </div>

                    {machineVideosUpdatedAt ? (
                      <p className="admin-settings-help">
                        Última atualização: {formatDateTime(machineVideosUpdatedAt)}
                      </p>
                    ) : null}
                  </section>
                </div>

                {error ? <div className="admin-settings-message error">{error}</div> : null}
                {success ? <div className="admin-settings-message success">{success}</div> : null}
              </article>

              <article className="admin-settings-panel">
                <div className="admin-settings-header compact">
                  <div>
                    <h2>Biblioteca cadastrada</h2>
                    <p>
                      {loadingMachineVideos
                        ? "Carregando vídeos..."
                        : `${machineVideos.length} vídeo(s) disponível(is) para a biblioteca`}
                    </p>
                  </div>
                </div>

                {loadingMachineVideos ? (
                  <div className="admin-settings-empty">Carregando vídeos de máquinas...</div>
                ) : machineVideos.length === 0 ? (
                  <div className="admin-settings-empty">
                    Nenhum vídeo foi enviado ainda. Cadastre o primeiro item no formulário acima.
                  </div>
                ) : (
                  <div className="admin-machine-videos-table">
                    <div className="admin-machine-videos-head">
                      <span>Ordem</span>
                      <span>Vídeo</span>
                      <span>Nome</span>
                      <span>Habilitado em</span>
                      <span>Origem</span>
                      <span>Ações</span>
                    </div>

                    <div className="admin-machine-videos-body">
                      {machineVideos.map((video, index) => {
                        const isEditing = editingMachineVideoId === video.id;
                        const isSavingRow = savingMachineVideoId === video.id;
                        const isDeletingRow = deletingMachineVideoId === video.id;
                        const isDragging = draggedMachineVideoId === video.id;

                        return (
                          <div
                            key={video.id}
                            className={`admin-machine-video-row${isDragging ? " is-dragging" : ""}`}
                            draggable
                            onDragStart={() => handleMachineVideoDragStart(video.id)}
                            onDragEnd={() => setDraggedMachineVideoId("")}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={() => handleMachineVideoDrop(video.id)}
                          >
                            <div className="admin-machine-video-order">
                              <span className="admin-machine-video-drag">⋮⋮</span>
                              <strong>{index + 1}</strong>
                            </div>

                            <div
                              className={`admin-machine-video-preview ${
                                video.aspectRatio === "9:16" ? "is-vertical" : "is-horizontal"
                              }`}
                            >
                              {isMachineVideoLink(video) ? (
                                <iframe
                                  src={video.embedUrl}
                                  title={video.title}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                  referrerPolicy="strict-origin-when-cross-origin"
                                  allowFullScreen
                                />
                              ) : (
                                <video src={video.url} controls preload="metadata" />
                              )}
                            </div>

<div className="admin-machine-video-name">
                              {isEditing ? (
                                <>
                                  <input
                                    type="text"
                                    value={editingMachineVideoTitle}
                                    onChange={(event) =>
                                      setEditingMachineVideoTitle(event.target.value)
                                    }
                                    placeholder="Nome do vídeo"
                                  />
                                  <div className="admin-audience-toggle-grid is-inline">
                                    <label className="admin-audience-toggle">
                                      <input
                                        type="checkbox"
                                        checked={editingMachineVideoLicensedEnabled}
                                        onChange={(event) =>
                                          setEditingMachineVideoLicensedEnabled(
                                            event.target.checked
                                          )
                                        }
                                      />
                                      <span
                                        className="admin-audience-toggle-switch"
                                        aria-hidden="true"
                                      />
                                      <span className="admin-audience-toggle-text">
                                        Licenciado
                                      </span>
                                    </label>
                                    <label className="admin-audience-toggle">
                                      <input
                                        type="checkbox"
                                        checked={editingMachineVideoRealgemsEnabled}
                                        onChange={(event) =>
                                          setEditingMachineVideoRealgemsEnabled(
                                            event.target.checked
                                          )
                                        }
                                      />
                                      <span
                                        className="admin-audience-toggle-switch"
                                        aria-hidden="true"
                                      />
                                      <span className="admin-audience-toggle-text">
                                        Realgems
                                      </span>
                                    </label>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <strong>{video.title}</strong>
                                  <small>
                                    {isMachineVideoLink(video) ? "YouTube" : "Arquivo enviado"} •{" "}
                                    {video.aspectRatio}
                                  </small>
                                  <small>Cadastrado em {formatDateTime(video.createdAt)}</small>
                                </>
                              )}
                            </div>

                            <div className="admin-machine-video-visibility">
                              <strong>{getMachineVideoAudienceSummary(video)}</strong>
                              <small>
                                {video.licensedEnabled && video.realgemsEnabled
                                  ? "Disponível para os dois acessos."
                                  : video.licensedEnabled
                                    ? "Exibido apenas no painel licenciado."
                                    : video.realgemsEnabled
                                      ? "Exibido apenas no portal Realgems."
                                      : "Nenhum acesso habilitado."}
                              </small>
                            </div>

                            <div className="admin-machine-video-meta">
                              <strong>
                                {isMachineVideoLink(video)
                                  ? "Link do YouTube"
                                  : video.originalName}
                              </strong>
                              <small>
                                {isMachineVideoLink(video)
                                  ? video.linkUrl
                                  : formatFileSize(video.size)}
                              </small>
                            </div>

                            <div className="admin-machine-video-actions">
                              {isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    className="is-primary"
                                    onClick={() => handleSaveMachineVideo(video.id)}
                                    disabled={isSavingRow}
                                  >
                                    {isSavingRow ? "Salvando..." : "Salvar"}
                                  </button>
                                  <button
                                    type="button"
                                    className="is-secondary"
                                    onClick={handleCancelEditMachineVideo}
                                    disabled={isSavingRow}
                                  >
                                    Cancelar
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="is-secondary"
                                  onClick={() => handleStartEditMachineVideo(video)}
                                  disabled={Boolean(editingMachineVideoId) || isDeletingRow}
                                >
                                  Editar
                                </button>
                              )}

                              <button
                                type="button"
                                className="is-danger"
                                onClick={() => handleDeleteMachineVideo(video.id)}
                                disabled={isSavingRow || isDeletingRow}
                              >
                                {isDeletingRow ? "Excluindo..." : "Excluir"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </article>
            </>
          ) : null}
        </section>
      </main>
    </div>
  );
};

export default AdminSettings;
